// CustomStarRatingComponent.js
import { Formio } from "formiojs";
import RadioComponent from "formiojs/components/radio/Radio";

class CustomStarRatingComponent extends RadioComponent {
  static schema(...extend) {
    return RadioComponent.schema({
      type: "radio",
      ...extend,
    });
  }

  get defaultSchema() {
    return CustomStarRatingComponent.schema();
  }

  // Check if this component is a star rating based on customClass
  isStarRating() {
    return this.component?.customClass?.includes("star-rating");
  }

  // Override emptyValue for star ratings to return 0 instead of ""
  get emptyValue() {
    if (this.isStarRating()) {
      return 0;
    }
    return super.emptyValue;
  }

  // Override isEmpty to handle zeroValid - 0 is not empty when zeroValid is true
  isEmpty(value = this.dataValue) {
    if (this.isStarRating() && this.component?.zeroValid) {
      // When zeroValid is true, only undefined/null/"" are empty, not 0
      return value === undefined || value === null || value === "";
    }
    return super.isEmpty(value);
  }

  // Override updateValue to prevent empty values for star ratings
  updateValue(value, flags = {}) {
    if (this.isStarRating()) {
      if (value === "" || value === null || value === undefined || value === "0") {
        value = 0;
      } else if (typeof value === "string") {
        value = parseInt(value, 10);
      }
    }
    return super.updateValue(value, flags);
  }

  // Override setValue to prevent empty values for star ratings
  setValue(value, flags = {}) {
    if (this.isStarRating()) {
      if (value === "" || value === null || value === undefined || value === "0") {
        value = 0;
      } else if (typeof value === "string") {
        value = parseInt(value, 10);
      }
    }
    const result = super.setValue(value, flags);

    // Update star display after value is set
    if (this.isStarRating() && this.element) {
      this.updateStarDisplay();
    }

    return result;
  }

  // Override attach to customize click behavior
  attach(element) {
    const superAttach = super.attach(element);

    if (this.isStarRating()) {
      setTimeout(() => {
        this.setupStarRating(element);
      }, 0);
    }

    return superAttach;
  }

  // Override the normalizeValue to prevent empty values
  normalizeValue(value) {
    const normalized = super.normalizeValue(value);
    if (this.isStarRating()) {
      if (
        normalized === "" ||
        normalized === null ||
        normalized === undefined ||
        normalized === "0"
      ) {
        return 0;
      }
      if (typeof normalized === "string") {
        return parseInt(normalized, 10);
      }
    }
    return normalized;
  }

  // Move updateStarDisplay to class method so it can be called from setValue
  updateStarDisplay() {
    const REMOVE_VALUE = 0;
    const STAR_FULL = "add_star_full";
    const STAR_LINE = "add_star_line";

    const starRating =
      this.element?.querySelector(".star-rating") || this.element?.closest(".star-rating");
    if (!starRating) return;

    const inputs = [...starRating.querySelectorAll(".form-radio label input")];
    const spans = inputs.map(input => input.nextElementSibling).filter(Boolean);

    // Get value from component, not from DOM checked state
    const currentValue = this.getValue() ?? 0;
    const isCleared = currentValue === REMOVE_VALUE || currentValue === "0";

    // Filter out remove button - compare as strings for DOM values
    const starInputs = inputs.filter(i => i.value !== String(REMOVE_VALUE));
    const selectedIndex = starInputs.findIndex(input => input.value === String(currentValue));

    spans.forEach((span, i) => {
      span.tabIndex = "0";
      const input = inputs[i];
      const isRemoveBtn = input.value === String(REMOVE_VALUE);
      const starPosition = starInputs.indexOf(input);

      span.className =
        isRemoveBtn || isCleared || selectedIndex === -1 || starPosition > selectedIndex
          ? STAR_LINE
          : STAR_FULL;
    });
  }

  setupStarRating(element) {
    const REMOVE_VALUE = 0;

    const starRating = element.querySelector(".star-rating") || element.closest(".star-rating");
    if (!starRating) return;

    const componentInstance = this;

    // Find all radio inputs and override their click behavior
    const inputs = starRating.querySelectorAll(".form-radio label input");

    inputs.forEach(input => {
      // Remove existing Formio click handlers by cloning
      const newInput = input.cloneNode(true);
      input.parentNode.replaceChild(newInput, input);

      // Add our own click handler
      newInput.addEventListener("click", e => {
        // Convert DOM string value to number for comparison and storage
        const clickedValue =
          newInput.value === String(REMOVE_VALUE) ? REMOVE_VALUE : parseInt(newInput.value, 10);
        const currentValue = componentInstance.getValue();

        // If clicking the same value, prevent default and do nothing (no deselection)
        if (clickedValue === currentValue) {
          e.preventDefault();
          return;
        }

        // Otherwise, set the new value (as number)
        componentInstance.setValue(clickedValue);

        setTimeout(() => {
          componentInstance.updateStarDisplay();
        }, 0);
      });
    });

    // Initial display - use component value, not DOM state
    this.updateStarDisplay();

    // Keyboard support
    starRating.addEventListener("keypress", e => {
      if ((e.key === "Enter" || e.key === " ") && e.target.closest("label")) {
        e.preventDefault();
        e.target.closest("label").click();
      }
    });

    // Listen for external value changes
    this.on("change", () => {
      this.updateStarDisplay();
    });
  }
}

// Register component - override default radio
Formio.registerComponent("radio", CustomStarRatingComponent);

export default CustomStarRatingComponent;
